import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('service:UploadQueue');

class UploadQueueService {
  private activeCount = 0;
  private waiters: Array<{ key: string; resolve: () => void }> = [];

  /** Wait until it's this upload's turn. Resolves immediately if no active upload. */
  async acquireSlot(key: string): Promise<void> {
    if (this.activeCount === 0) {
      this.activeCount = 1;
      logger.info('slot-acquired', { key, queueDepth: 0 });
      return;
    }
    logger.info('slot-queued', {
      key,
      queueDepth: this.waiters.length + 1,
    });
    return new Promise<void>(resolve => {
      this.waiters.push({
        key,
        resolve: () => {
          logger.info('slot-acquired-from-queue', {
            key,
            remainingWaiters: this.waiters.length,
          });
          resolve();
        },
      });
    });
  }

  /** Release the active slot. Starts the next queued upload if any. */
  releaseSlot(): void {
    const next = this.waiters.shift();
    if (next) {
      logger.info('slot-released-next', {
        nextKey: next.key,
        remaining: this.waiters.length,
      });
      next.resolve();
    } else {
      this.activeCount = 0;
      logger.info('slot-released-idle');
    }
  }

  /** Remove a queued entry (e.g. user cancelled). No-op if not queued. */
  removeFromQueue(key: string): boolean {
    const idx = this.waiters.findIndex(w => w.key === key);
    if (idx === -1) return false;
    this.waiters.splice(idx, 1);
    logger.info('slot-removed', { key, remaining: this.waiters.length });
    return true;
  }

  /** Update a queued entry's key (when temp modalId is replaced by real storyId). */
  updateKey(oldKey: string, newKey: string): void {
    const entry = this.waiters.find(w => w.key === oldKey);
    if (entry) entry.key = newKey;
  }

  get queueDepth(): number {
    return this.waiters.length;
  }

  /** Reset internal state. For testing only. */
  _reset(): void {
    this.activeCount = 0;
    this.waiters = [];
  }
}

export const uploadQueueService = new UploadQueueService();
